import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, ReverLoading, WarningCircleIcon } from '@itsrever/design-system'
import { clsx } from 'clsx'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'
import { useIsReverStaff } from '@/hooks/use-is-rever-staff'
import { captureReturnsAndRefundsEvent } from '@/infrastructure/posthog'
import { Layered } from '@/domain/group-settings'
import {
  DEFAULT_GROUP_RETURN_CONDITIONS,
  GroupReturnConditions
} from '@/domain/return-conditions'
import { DEFAULT_REFUND_CONFIGURATION, RefundsSettings } from '@/domain/refund'
import { ReturnReasonsSettings } from '@/domain/return-reasons'
import {
  useFullCatalogPayments,
  useGroupSettings
} from '@/application/manage-group-settings'
import {
  GroupSettingsApi,
  refundsSettingsApi,
  returnConditionsWithPortalsApi,
  returnReasonsSettingsApi
} from '@/infrastructure/api/returns-and-refunds'
import {
  FormProps,
  LayeredSettings,
  LayeredSettingsProps,
  Scope
} from '@/ui/components/group-settings'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { LoadError, PageHeader } from '@/ui/shared'
import { ReturnReasonsProvider } from '@/ui/components/warehouses/condition-pill'
import {
  RequirementsForm,
  ReturnConditionsForm,
  useConditionsProblem
} from './return-conditions-forms'
import { NonReturnableForm } from './non-returnable-form'
import { ReasonsForm } from './reasons-form'
import { RefundsForm, useRefundsProblem } from './refunds-form'
import { useReturnReasonChoice } from './use-return-reason-choice'

export type ReturnsAndRefundsTab =
  | 'conditions'
  | 'reasons'
  | 'non-returnable'
  | 'requirements'
  | 'refunds'

// The setting each tab edits; return conditions span three tabs, each saving the whole conditions.
export type ReturnsAndRefundsSetting = 'conditions' | 'reasons' | 'refunds'

export const TAB_SETTINGS: Record<
  ReturnsAndRefundsTab,
  ReturnsAndRefundsSetting
> = {
  conditions: 'conditions',
  'non-returnable': 'conditions',
  requirements: 'conditions',
  reasons: 'reasons',
  refunds: 'refunds'
}

interface ReturnsAndRefundsProps {
  scope: Scope
  tab: ReturnsAndRefundsTab
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}

export const ReturnsAndRefunds: React.FC<ReturnsAndRefundsProps> = (props) => {
  const { reasons: reasonsLive } = useReturnsAndRefundsV2()
  // Only once the group's reasons are on v2: until then the portal offers other ones.
  const reasonChoice = useReturnReasonChoice(
    props.scope,
    reasonsLive && (props.tab === 'requirements' || props.tab === 'refunds')
  )
  return (
    <ReturnReasonsProvider value={reasonChoice}>
      <ReturnsAndRefundsTabs {...props} />
    </ReturnReasonsProvider>
  )
}

const ReturnsAndRefundsTabs: React.FC<ReturnsAndRefundsProps> = (props) => {
  // Rebuilt only when the portals change, not on every render of a new but equal list.
  const stores = props.scope.stores.join(',')
  const conditionsApi = useMemo(
    () => returnConditionsWithPortalsApi(stores ? stores.split(',') : []),
    [stores]
  )
  const { conditions: conditionsLive } = useReturnsAndRefundsV2()
  const reasonsApi = useMemo(
    () => returnReasonsSettingsApi(conditionsLive),
    [conditionsLive]
  )
  const conditionsProblem = useConditionsProblem()
  const refundsProblem = useRefundsProblem()
  const { t } = useTranslation()
  const showDialog = useDialog()
  const fullCatalog = useFullCatalogPayments(
    props.scope.group,
    props.scope.stores
  )
  const ecommerceId =
    props.scope.kind === 'store' ? props.scope.ecommerceId : undefined
  const paymentsTurnedOff = (
    draft: GroupReturnConditions,
    saved: GroupReturnConditions
  ) => saved.paymentsEnabled && !draft.paymentsEnabled
  switch (props.tab) {
    case 'conditions':
    case 'non-returnable':
    case 'requirements':
      return (
        <Setting<GroupReturnConditions>
          {...props}
          name="returnConditions"
          ns="returns_refunds.conditions"
          api={conditionsApi}
          defaults={DEFAULT_GROUP_RETURN_CONDITIONS}
          problem={conditionsProblem}
          // Full Catalog needs payments: asks only where it is on, since saving moves it to same product.
          confirmSave={async (draft, saved) =>
            !paymentsTurnedOff(draft, saved) ||
            (await fullCatalog.fullCatalogLayers(ecommerceId)).layers.length ===
              0 ||
            showDialog({
              title: t('returns_refunds.conditions.payments_off_title'),
              message: (
                <Base.Callout
                  tone="danger"
                  icon={<WarningCircleIcon />}
                  className="text-xs"
                >
                  {t('returns_refunds.conditions.payments_off_message')}
                </Base.Callout>
              ),
              okButtonText: t('returns_refunds.conditions.payments_off_ok'),
              cancelButtonText: t('group_settings.cancel'),
              destructive: true
            })
          }
          saveAlso={async (draft, saved) => {
            if (paymentsTurnedOff(draft, saved))
              await fullCatalog.switchFullCatalogOff(ecommerceId)
          }}
          saveAlsoError={t('returns_refunds.conditions.payments_off_error')}
          render={(form) =>
            props.tab === 'conditions' ? (
              <ReturnConditionsForm {...form} />
            ) : props.tab === 'non-returnable' ? (
              <NonReturnableForm {...form} />
            ) : (
              <RequirementsForm {...form} />
            )
          }
        />
      )
    case 'reasons':
      return (
        <Setting<ReturnReasonsSettings>
          {...props}
          name="returnReasonsSettings"
          ns="returns_refunds.reasons"
          api={reasonsApi}
          defaults={{ reasons: [] }}
          render={(form) => <ReasonsForm {...form} />}
        />
      )
    case 'refunds':
      return (
        <Setting<RefundsSettings>
          {...props}
          name="refundsSettings"
          ns="returns_refunds.refunds"
          api={refundsSettingsApi}
          defaults={DEFAULT_REFUND_CONFIGURATION}
          problem={refundsProblem}
          render={(form) => <RefundsForm {...form} />}
        />
      )
  }
}

function Setting<T extends object>({
  scope,
  tab,
  name,
  ns,
  api,
  defaults,
  problem,
  confirmSave,
  saveAlso,
  saveAlsoError,
  render,
  onShowGroup,
  onShowStore
}: ReturnsAndRefundsProps &
  Pick<
    LayeredSettingsProps<T>,
    'confirmSave' | 'saveAlso' | 'saveAlsoError'
  > & {
    name: string
    ns: string
    api: GroupSettingsApi<T>
    defaults: T
    problem?: (draft: T) => string | undefined
    render: (form: FormProps<T>) => ReactNode
  }) {
  const { t } = useTranslation()
  const isReverUser = useIsReverStaff()
  // Who saves and where, for the share of edits made by merchants and at group level.
  const tracking = { setting: TAB_SETTINGS[tab], tab, isReverUser }
  const { layered, loading, failed, reload, actions } = useGroupSettings(
    name,
    api,
    scope.group,
    {
      saveSuccess: `${ns}.save_success`,
      saveError: `${ns}.save_error`,
      resetSuccess: 'returns_refunds.reset_success',
      resetError: 'returns_refunds.reset_error'
    },
    { properties: tracking, stores: scope.stores }
  )
  const ecommerceId = scope.kind === 'store' ? scope.ecommerceId : undefined
  const viewKey = [tab, scope.group, scope.kind, ecommerceId].join('|')
  const capturedView = useRef<string>()
  // Once per tab and scope, as on Exchanges: the refetch after a save is the same view.
  useEffect(() => {
    if (failed || !layered || capturedView.current === viewKey) return
    capturedView.current = viewKey
    captureReturnsAndRefundsEvent('Returns and refunds viewed', {
      group: scope.group,
      ...tracking,
      level: scope.kind,
      ecommerceId,
      storeCount: scope.stores.length,
      storeOverrideCount: Object.keys(layered.overrides).length,
      hasGroupSettings: !!layered.group
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layered, failed, viewKey])
  const header = (
    <PageHeader
      title={t(`returns_refunds.tabs.${tab}`)}
      description={t(`returns_refunds.descriptions.${tab}`)}
      actions={
        scope.kind === 'store' ? (
          <Base.Badge tone="info">
            {t('returns_refunds.scope_store', { store: scope.ecommerceId })}
          </Base.Badge>
        ) : undefined
      }
    />
  )
  if (loading) return <ReverLoading />
  // A failed load must not read as a group without settings: saving would replace them with defaults.
  if (failed || !layered) {
    return (
      <>
        {header}
        <div className="px-7 py-6">
          <LoadError message={t(`${ns}.load_error`)} onRetry={() => reload()} />
        </div>
      </>
    )
  }
  return (
    <>
      {header}
      <div
        className={clsx(
          'flex flex-col gap-4 px-7 py-4',
          tab === 'conditions' || tab === 'non-returnable'
            ? 'max-w-3xl'
            : 'max-w-5xl'
        )}
      >
        <LayeredSettings<T>
          scope={scope}
          layered={layered as Layered<T>}
          defaults={defaults}
          actions={actions}
          problem={problem}
          confirmSave={confirmSave}
          saveAlso={saveAlso}
          saveAlsoError={saveAlsoError}
          render={render}
          onShowGroup={onShowGroup}
          onShowStore={onShowStore}
        />
      </div>
    </>
  )
}
